import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Popconfirm, Space, Table, Tag, Tooltip, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import * as api from '@apis/grouping-agent';
import { DryRunResponse, ExecuteRunResponse, GroupingAgentRun, RoutingDecision } from '@models/grouping-agent';
import { formatGroupingRunDuration } from '@utils/grouping-agent';
import {
  ROUTING_MODE_COLOR,
  RUN_STATUS_COLOR,
  invocationTypeLabel,
  routingModeLabel,
  runStatusLabel,
} from '@utils/reservation-success-labels';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';

const GroupingAgentRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [dryRunData, setDryRunData] = useState<DryRunResponse | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['grouping-agent-runs'],
    queryFn: () => api.listGroupingAgentRuns({ limit: 50 }),
    refetchInterval: (query) =>
      (query.state.data?.data as GroupingAgentRun[] | undefined)?.some((r) => r.status === 'running') ? 5000 : false,
  });

  const dryRunMutation = useMutation({
    mutationFn: () => api.runGroupingAgent({ dryRun: true, mode: 'auto' }),
    onSuccess: (res) => {
      setDryRunData(res.data as DryRunResponse);
      setDryRunOpen(true);
    },
    onError: () => message.error('배정 미리보기 실패'),
  });

  const handleRunResult = (body: ExecuteRunResponse, forced = false) => {
    if (body.result === 'SKIPPED') {
      if (body.reason === 'no_assignment_gap') {
        const rs = body.routingSummary;
        message.info(
          `배정 갭이 없어 실행하지 않았습니다. (전체 재배정 ${rs?.full ?? 0} / 증분 ${rs?.incremental ?? 0} / 생략 ${
            rs?.skipped ?? 0
          })`,
        );
        return;
      }
      message.warning(`실행 스킵: ${body.reason} (active: ${body.activeRunId?.slice(0, 8)})`);
      return;
    }
    message.success(forced ? '강제 그룹핑 실행이 시작되었습니다.' : '그룹핑 실행이 시작되었습니다.');
    queryClient.invalidateQueries({ queryKey: ['grouping-agent-runs'] });
    if (body.runId) {
      setTimeout(() => navigate(`/grouping-agent-runs/${body.runId}`), 1500);
    }
  };

  const runMutation = useMutation({
    mutationFn: () => api.runGroupingAgent({ mode: 'auto', invocationType: 'manual' }),
    onSuccess: (res) => handleRunResult(res.data as ExecuteRunResponse),
    onError: () => message.error('실행 실패'),
  });

  const forceRunMutation = useMutation({
    mutationFn: () => api.runGroupingAgent({ mode: 'auto', invocationType: 'manual', force: true }),
    onSuccess: (res) => handleRunResult(res.data as ExecuteRunResponse, true),
    onError: () => message.error('강제 실행 실패'),
  });

  const runs: GroupingAgentRun[] = data?.data || [];

  const columns: ColumnsType<GroupingAgentRun> = [
    {
      title: 'Run ID',
      dataIndex: 'runId',
      render: (id: string) => (
        <Button type="link" onClick={() => navigate(`/grouping-agent-runs/${id}`)}>
          {id.slice(0, 8)}…
        </Button>
      ),
    },
    {
      title: '호출',
      dataIndex: 'invocationType',
      render: (v: string) => invocationTypeLabel(v),
    },
    {
      title: '기준일',
      dataIndex: 'triggerDate',
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-'),
    },
    {
      title: '상태',
      dataIndex: 'status',
      render: (s: string) => <Tag color={RUN_STATUS_COLOR[s]}>{runStatusLabel(s)}</Tag>,
    },
    {
      title: <Tooltip title="전체 재배정 / 증분 / 생략 회차 수">배정 방식</Tooltip>,
      render: (_, r) =>
        r.routingSummary ? `${r.routingSummary.full}/${r.routingSummary.incremental}/${r.routingSummary.skipped}` : '-',
    },
    {
      title: '보충필요',
      render: (_, r) => (
        <Tag color={r.summary?.shortGroupCount ? 'red' : 'default'}>{r.summary?.shortGroupCount ?? '-'}</Tag>
      ),
    },
    {
      title: '시작',
      dataIndex: 'triggeredAt',
      render: (v) => (v ? dayjs(v).format('MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '소요 시간',
      render: (_, r) => {
        const text = formatGroupingRunDuration(r.triggeredAt, r.completedAt);
        if (r.status === 'running') {
          return (
            <span>
              {text} <Tag color="processing">진행 중</Tag>
            </span>
          );
        }
        return text;
      },
    },
    {
      title: '완료',
      dataIndex: 'completedAt',
      render: (v) => (v ? dayjs(v).format('MM-DD HH:mm:ss') : '-'),
    },
  ];

  const routingColumns: ColumnsType<RoutingDecision> = [
    { title: '회차 키', dataIndex: 'scheduleKey', ellipsis: true },
    {
      title: '배정 방식',
      dataIndex: 'mode',
      render: (m: string) => <Tag color={ROUTING_MODE_COLOR[m]}>{routingModeLabel(m)}</Tag>,
    },
    { title: '사유', dataIndex: 'reason', ellipsis: true },
  ];

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title="그룹핑 실행"
        description="결제 건을 그룹에 넣는 실행 이력입니다. 성사 진단은 배치 실행에서 확인합니다. 진행 중이면 5초마다 갱신됩니다."
        extra={
          <Space>
            <Button onClick={() => refetch()}>새로고침</Button>
            <Tooltip title="저장하지 않고, 회차마다 전체 재배정·증분·생략 중 무엇을 할지 미리 봅니다.">
              <Button loading={dryRunMutation.isPending} onClick={() => dryRunMutation.mutate()}>
                배정 미리보기
              </Button>
            </Tooltip>
            <Popconfirm
              title="그룹핑을 지금 실행할까요?"
              description="대상 회차의 그룹 배정이 실제로 저장됩니다."
              okText="실행"
              cancelText="취소"
              onConfirm={() => runMutation.mutate()}>
              <Button type="primary" loading={runMutation.isPending}>
                그룹핑 실행
              </Button>
            </Popconfirm>
            <Popconfirm
              title="그룹핑을 강제로 실행할까요?"
              description="진행 중인 실행이 있어도 다시 돌립니다. 배정이 겹칠 수 있습니다."
              okText="실행"
              cancelText="취소"
              onConfirm={() => forceRunMutation.mutate()}>
              <Button loading={forceRunMutation.isPending}>강제 실행</Button>
            </Popconfirm>
          </Space>
        }
      />

      <Table rowKey="runId" loading={isLoading} columns={columns} dataSource={runs} pagination={{ pageSize: 20 }} />

      <Modal title="배정 미리보기" open={dryRunOpen} onCancel={() => setDryRunOpen(false)} width={960} footer={null}>
        {dryRunData?.routingSummary && (
          <p>
            전체 재배정 {dryRunData.routingSummary.full} · 증분 {dryRunData.routingSummary.incremental} · 생략{' '}
            {dryRunData.routingSummary.skipped}
            {dryRunData.assignmentGapCount != null && ` · 배정 갭 ${dryRunData.assignmentGapCount}건`}
            {dryRunData.summary?.targetScheduleCount != null &&
              ` · 대상 회차 ${dryRunData.summary.targetScheduleCount}건`}
          </p>
        )}
        <Table
          size="small"
          rowKey="scheduleKey"
          columns={routingColumns}
          dataSource={dryRunData?.routingDecisions || []}
          pagination={{ pageSize: 15 }}
        />
      </Modal>
    </div>
  );
};

export default GroupingAgentRunsPage;
